/**
 * Theme preference.
 *
 * Three states, not two: `light`, `dark`, and `system`. A binary toggle cannot
 * express "follow my OS", which is what most people actually want — and which
 * means the choice has to survive the OS changing at sunset.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'naijaindex.theme'

/**
 * The same key, as a cookie.
 *
 * The server-rendered catalogue cannot read localStorage, and those pages ship
 * no JavaScript at all — so without this a visitor who chose dark in the app
 * would land on a catalogue page and watch it switch back. Two surfaces of one
 * product must not disagree about something the visitor explicitly chose.
 *
 * A year, `Lax`, and readable by script: it is a display preference, not a
 * credential.
 */
function writeCookie(value: ThemePreference): void {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      `${STORAGE_KEY}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${secure}`
  } catch {
    /* a browser with cookies disabled still gets the localStorage copy */
  }
}
const listeners = new Set<() => void>()

let preference: ThemePreference = readStored()

function isPreference(value: string | null | undefined): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

/** The cookie's value, or null. */
function readCookie(): ThemePreference | null {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${STORAGE_KEY.replace('.', '\\.')}=([^;]*)`),
    )
    const value = match ? decodeURIComponent(match[1]) : null
    return isPreference(value) ? value : null
  } catch {
    return null
  }
}

/**
 * The stored preference, cookie first.
 *
 * The cookie leads because it is the only channel both surfaces can write. The
 * catalogue pages ship no JavaScript, so their toggle is a link that sets the
 * cookie and nothing else — reading localStorage first meant that choice was
 * ignored here, and then overwritten on the next load by the stale value. The
 * visitor pressed the toggle, watched the page go dark, and found it light
 * again when they navigated back into the app.
 *
 * localStorage remains the fallback for a browser that refuses cookies.
 */
function readStored(): ThemePreference {
  const cookie = readCookie()
  if (cookie) return cookie
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isPreference(stored)) return stored
  } catch {
    /* private browsing */
  }
  return 'system'
}

function prefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

export function resolveTheme(value: ThemePreference): 'light' | 'dark' {
  return value === 'system' ? (prefersDark() ? 'dark' : 'light') : value
}

function apply(): void {
  const resolved = resolveTheme(preference)
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  // Keeps the browser chrome (scrollbars, form controls) in step with the page.
  document.documentElement.style.colorScheme = resolved
}

export function setTheme(next: ThemePreference): void {
  preference = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* ignore */
  }
  writeCookie(next)
  apply()
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useTheme() {
  const value = useSyncExternalStore(subscribe, () => preference, () => 'system' as const)

  // While the preference is `system`, the OS can change under us — at sunset,
  // or when the user flips their own setting. Without this listener the page
  // would stay on whichever theme it happened to load with.
  useEffect(() => {
    if (value !== 'system') return
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      apply()
      listeners.forEach((listener) => listener())
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [value])

  const cycle = useCallback(() => {
    setTheme(resolveTheme(preference) === 'dark' ? 'light' : 'dark')
  }, [])

  return { preference: value, resolved: resolveTheme(value), setTheme, cycle }
}

/**
 * Applied before React mounts.
 *
 * Called from `main.tsx` so the correct theme is on the html element during the
 * first paint. Applying it in an effect instead produces a white flash on every
 * load for dark-mode users.
 */
export function initTheme(): void {
  apply()
  // Also republish it. The cookie used to be written only by the toggle, so a
  // preference already in localStorage — set before this cookie existed, or
  // surviving a cookie clear — was invisible to the server-rendered pages: the
  // app rendered dark and every catalogue page rendered light. Writing it here
  // reconciles the two surfaces on every load rather than only when someone
  // happens to press the toggle again.
  writeCookie(preference)
}
